import { useState, useEffect } from 'react'
import {
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material'
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  HourglassEmpty as HourglassEmptyIcon
} from '@mui/icons-material'
import api from '../api'

interface Deployment {
  DeploymentID: number
  CustomerID: number
  CompanyName: string
  LicenseScenario: string
  Status: string
  StartDate: string
  EndDate?: string
  TotalPolicies: number
  SuccessfulPolicies: number
  FailedPolicies: number
  SkippedPolicies: number
  ErrorMessage?: string
  SubscriptionType: string
}

interface DeploymentDetails {
  deployment: Deployment
  policies: PolicyDetail[]
  totalPolicies: number
}

interface PolicyDetail {
  CustomerPolicyID: number
  PolicyID: number
  PolicyName: string
  Platform: string
  PolicyType: string
  DeploymentStatus: string
  DeployedDate: string
  PolicyObjectID?: string
  ErrorMessage?: string
  IsCustomized: boolean
}

export default function Deployments() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deployments, setDeployments] = useState<Deployment[]>([])
  const [filteredDeployments, setFilteredDeployments] = useState<Deployment[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('All')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [selectedDeployment, setSelectedDeployment] = useState<DeploymentDetails | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [retryLoading, setRetryLoading] = useState(false)
  const [triggerDialogOpen, setTriggerDialogOpen] = useState(false)

  useEffect(() => {
    loadDeployments()
  }, [])

  useEffect(() => {
    filterDeployments()
  }, [deployments, searchTerm, statusFilter])

  const loadDeployments = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await api.get('/TCAdminDeployments', {
        params: { limit: 500 }
      })
      
      setDeployments(response.data.deployments)
    } catch (err: any) {
      console.error('Error loading deployments:', err)
      setError(err.response?.data || err.message || 'Failed to load deployments')
    } finally {
      setLoading(false)
    }
  }

  const filterDeployments = () => {
    let filtered = [...deployments]

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (d) =>
          d.CompanyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          d.LicenseScenario.toLowerCase().includes(searchTerm.toLowerCase()) ||
          d.DeploymentID.toString().includes(searchTerm)
      )
    }

    // Status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter((d) => d.Status === statusFilter)
    }

    setFilteredDeployments(filtered)
    setPage(0)
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleViewDetails = async (deployment: Deployment) => {
    try {
      setDetailsLoading(true)
      setDialogOpen(true)
      
      const response = await api.get(`/TCAdminDeployments/${deployment.DeploymentID}/details`)
      
      setSelectedDeployment(response.data)
    } catch (err: any) {
      console.error('Error loading deployment details:', err)
      setError(err.response?.data || err.message || 'Failed to load deployment details')
    } finally {
      setDetailsLoading(false)
    }
  }

  const handleRetryDeployment = async (deploymentId: number) => {
    if (!confirm('Are you sure you want to retry this deployment? This will create a new deployment for failed/skipped policies.')) {
      return
    }

    try {
      setRetryLoading(true)
      
      const response = await api.post(
        `/TCAdminDeployments/${deploymentId}/retry`,
        {}
      )
      
      alert(`Deployment retry successful! New deployment ID: ${response.data.newDeploymentId}`)
      handleCloseDialog()
      await loadDeployments()
    } catch (err: any) {
      console.error('Error retrying deployment:', err)
      alert(err.response?.data || err.message || 'Failed to retry deployment')
    } finally {
      setRetryLoading(false)
    }
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setSelectedDeployment(null)
  }

  const getStatusChip = (status: string) => {
    const statusMap: Record<string, { color: any; icon: any }> = {
      Completed: { color: 'success', icon: <CheckCircleIcon /> },
      Failed: { color: 'error', icon: <ErrorIcon /> },
      Partial: { color: 'warning', icon: <WarningIcon /> },
      'In Progress': { color: 'info', icon: <HourglassEmptyIcon /> },
      Pending: { color: 'default', icon: <HourglassEmptyIcon /> }
    }
    const config = statusMap[status] || { color: 'default', icon: null }
    return <Chip label={status} color={config.color} size="small" icon={config.icon} />
  }

  const getPolicyStatusChip = (status: string) => {
    const statusMap: Record<string, any> = {
      Deployed: 'success',
      Failed: 'error',
      Skipped: 'warning',
      Modified: 'info',
      Pending: 'default'
    }
    return <Chip label={status} color={statusMap[status] || 'default'} size="small" />
  }

  const getPlatformChip = (platform: string) => {
    const colors: Record<string, any> = {
      Windows: 'primary',
      macOS: 'secondary',
      Android: 'success',
      iOS: 'info',
      Base: 'default'
    }
    return <Chip label={platform} color={colors[platform] || 'default'} size="small" variant="outlined" />
  }

  // Calculate statistics
  const stats = {
    total: deployments.length,
    completed: deployments.filter((d) => d.Status === 'Completed').length,
    failed: deployments.filter((d) => d.Status === 'Failed').length,
    inProgress: deployments.filter((d) => d.Status === 'In Progress' || d.Status === 'Pending').length,
    partial: deployments.filter((d) => d.Status === 'Partial').length
  }

  // Get unique statuses for filter
  const statuses = ['All', ...new Set(deployments.map((d) => d.Status))]

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Deployments
        </Typography>
        <LinearProgress sx={{ mt: 2 }} />
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          Loading deployments...
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Deployments
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
        <Button onClick={loadDeployments} variant="contained" sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Deployments</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadDeployments}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={() => setTriggerDialogOpen(true)}
          >
            Trigger Deployment
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" paragraph>
        View and manage all customer deployments
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={2.4}>
          <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4">{stats.total}</Typography>
            <Typography variant="body2" color="text.secondary">
              Total
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">
              {stats.completed}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Completed
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="error.main">
              {stats.failed}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Failed
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main">
              {stats.partial}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Partial
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="info.main">
              {stats.inProgress}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              In Progress
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                placeholder="Search by company name, license scenario, or deployment ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {statuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Results Summary */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredDeployments.length} of {deployments.length} deployments
            </Typography>
          </Box>

          {/* Deployments Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>License Scenario</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>Policies</TableCell>
                  <TableCell>Success Rate</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDeployments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No deployments found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {deployments.length === 0
                          ? 'No deployments have been created yet.'
                          : 'No deployments match your current filters.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDeployments
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((deployment) => (
                      <TableRow key={deployment.DeploymentID} hover>
                        <TableCell>{deployment.DeploymentID}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {deployment.CompanyName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {deployment.CustomerID}
                          </Typography>
                        </TableCell>
                        <TableCell>{deployment.LicenseScenario}</TableCell>
                        <TableCell>{getStatusChip(deployment.Status)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(deployment.StartDate).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{deployment.TotalPolicies}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="success.main">
                              {deployment.SuccessfulPolicies}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              /
                            </Typography>
                            <Typography variant="body2" color="error.main">
                              {deployment.FailedPolicies}
                            </Typography>
                            {deployment.SkippedPolicies > 0 && (
                              <>
                                <Typography variant="body2" color="text.secondary">
                                  /
                                </Typography>
                                <Typography variant="body2" color="warning.main">
                                  {deployment.SkippedPolicies}
                                </Typography>
                              </>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={deployment.SubscriptionType} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(deployment)}
                              color="primary"
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          {(deployment.Status === 'Failed' || deployment.Status === 'Partial') && (
                            <Tooltip title="Retry Deployment">
                              <IconButton
                                size="small"
                                onClick={() => handleRetryDeployment(deployment.DeploymentID)}
                                color="warning"
                                disabled={retryLoading}
                              >
                                <RefreshIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredDeployments.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>

      {/* Deployment Details Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          Deployment Details
          {selectedDeployment && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Deployment ID: {selectedDeployment.deployment.DeploymentID} | {selectedDeployment.deployment.CompanyName}
              </Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {detailsLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading deployment details...
              </Typography>
            </Box>
          ) : selectedDeployment ? (
            <Box>
              {/* Deployment Summary */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Box sx={{ mt: 1 }}>{getStatusChip(selectedDeployment.deployment.Status)}</Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Policies
                    </Typography>
                    <Typography variant="h6">{selectedDeployment.deployment.TotalPolicies}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Successful
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {selectedDeployment.deployment.SuccessfulPolicies}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Failed
                    </Typography>
                    <Typography variant="h6" color="error.main">
                      {selectedDeployment.deployment.FailedPolicies}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Error Message */}
              {selectedDeployment.deployment.ErrorMessage && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight="medium">
                    Deployment Error:
                  </Typography>
                  <Typography variant="body2">{selectedDeployment.deployment.ErrorMessage}</Typography>
                </Alert>
              )}

              {/* Policy Details */}
              <Typography variant="h6" gutterBottom>
                Policy Deployment Details
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Policy Name</TableCell>
                      <TableCell>Platform</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Deployed Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedDeployment.policies.map((policy) => (
                      <TableRow key={policy.CustomerPolicyID}>
                        <TableCell>
                          <Typography variant="body2">{policy.PolicyName}</Typography>
                          {policy.ErrorMessage && (
                            <Accordion sx={{ mt: 1 }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="caption" color="error">
                                  Error Details
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                  {policy.ErrorMessage}
                                </Typography>
                              </AccordionDetails>
                            </Accordion>
                          )}
                        </TableCell>
                        <TableCell>{getPlatformChip(policy.Platform)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{policy.PolicyType}</Typography>
                        </TableCell>
                        <TableCell>{getPolicyStatusChip(policy.DeploymentStatus)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(policy.DeployedDate).toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          {selectedDeployment &&
            (selectedDeployment.deployment.Status === 'Failed' ||
              selectedDeployment.deployment.Status === 'Partial') && (
              <Button
                onClick={() => handleRetryDeployment(selectedDeployment.deployment.DeploymentID)}
                variant="contained"
                color="warning"
                startIcon={<RefreshIcon />}
                disabled={retryLoading}
              >
                {retryLoading ? 'Retrying...' : 'Retry Deployment'}
              </Button>
            )}
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Trigger Deployment Dialog - Placeholder for now */}
      <Dialog open={triggerDialogOpen} onClose={() => setTriggerDialogOpen(false)}>
        <DialogTitle>Trigger Manual Deployment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Manual deployment trigger UI coming soon...
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTriggerDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
